/**
 * Village life: summoned villagers go out and DO their job on the farm map.
 *
 * An idle villager strolls near its home. An ASSIGNED one runs a real work loop:
 * it looks for a node its trade cares about (a forester wants trees, a miner
 * rocks, a forager mushrooms and bushes), walks there, swings its tool for a
 * while, then heads back toward its workplace and goes again. Before this they
 * only milled about within 1.5 tiles of the workplace, so a villager you had paid
 * for and assigned was indistinguishable from one doing nothing.
 *
 * IMPORTANT — this is a performance, not production. The resources a villager
 * earns are accrued lazily on the SERVER from its rate and the time elapsed
 * (village.service), exactly as before. The on-screen chopping does not fell the
 * tree, does not touch HarvestState and pays nothing: if it did, the same wood
 * would be credited twice and the world would desync from the server's idea of
 * it. The loop exists so you can SEE who is working and on what.
 *
 * They mount into the shared y-sorted entity layer so they sort with trees/hero
 * by feet-Y, and flip horizontally to face their walk direction.
 */
import { Assets, Container, Sprite, Texture, Rectangle } from 'pixi.js';
import { TILE } from './world';

const FRAMES = 6;
const IDLE_FPS = 4;
const WALK_FPS = 8;
const SCALE = TILE / 20;            // ~20px art → ~1 tile tall
const SPEED = 1.0;                  // tiles/sec — a slow stroll (well under the player's 4)
const WANDER_RADIUS = 4;            // tiles from home when idle
const PAUSE = { min: 2200, max: 6500 };
const WORK_FPS = 9;                 // the tool swing — brisker than the walk
/** How far from its workplace a villager will roam looking for something to work. */
const SEARCH_TILES = 14;
/** Swing at a node for this long before wandering off to the next one. */
const WORK_MS = { min: 4200, max: 8000 };
/** Close enough to a node to start swinging (its tile is solid, so never reach 0). */
const WORK_REACH = TILE * 1.15;
/** Give up walking to a node after this long — it may be unreachable behind water. */
const TRAVEL_TIMEOUT_MS = 14_000;

/**
 * Synthetic node kinds. The three original trades all work WORLDGEN DECOR, which
 * the scene indexes in its decor buckets; the two task hires do not — a farmer
 * works plot rows that live on `c.farm` and a herder works owned animals that
 * live in the livestock layer. Rather than teach this module about either, they
 * are named as pseudo-kinds and the scene's findWorkNode routes them (see
 * views/farm/scene.ts). Named constants, not string literals, because the two
 * sides must agree exactly or the villager silently reverts to strolling.
 */
export const PLOT_NODE = 'crop_bed';
export const PEN_ANIMAL_NODE = 'pen_animal';

/** What each trade goes looking for. Keys are worldgen decor kinds. */
export const JOB_TARGETS: Record<string, string[]> = {
  forester: ['tree'],
  miner: ['ore_rock', 'boulder', 'rock'],
  forager: ['mushrooms', 'bush'],
  // The task hires. Same performance, same disclaimer: the farmer's kneeling does
  // not water the bed and the herder's swing does not feed the cow — the server
  // does both, when the standing order is RUN (POST /village/task/run).
  farmer: [PLOT_NODE],
  herder: [PEN_ANIMAL_NODE],
};

/**
 * Villager given names — deliberately NOT the arena roster.
 *
 * arena/roster.ts is a list of player-style HANDLES ("Criminal777", "wagmi77")
 * and arena/names.ts generates more of the same. Those read as accounts in a
 * lobby, which is exactly right for a gladiator and exactly wrong for the woman
 * chopping your trees: a farmhand called `Shroomblade99` breaks the pastoral
 * frame the farm is built on, and worse, it implies the two are the same kind of
 * entity. Villagers get plain rural given names instead, in the same register as
 * the server's own pool (village.service NAMES) and the animal names.
 *
 * The server names every Villager row on summon and persists it, so this pool is
 * only ever the fallback for a row that reached the client without one (an older
 * farm, a partial payload). It is keyed off the villager's persisted id, so the
 * fallback is stable for the life of that villager too — a name that changed on
 * reload would be worse than none.
 */
export const VILLAGER_NAMES: readonly string[] = [
  'Bramble', 'Hazel', 'Pip', 'Rowan', 'Fern', 'Clover', 'Mabel', 'Otis', 'Nettle', 'Bruni',
  'Sorrel', 'Tansy', 'Wren', 'Cob', 'Marigold', 'Dilly', 'Fennel', 'Juniper', 'Poppy', 'Basil',
  'Alder', 'Thistle', 'Mira', 'Barley', 'Ivy', 'Hollis', 'Quill', 'Rue', 'Saffy', 'Tamsin',
];

/** FNV-1a over the id — any stable spread will do; this one needs no dependency. */
function hashId(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * The name to show for a villager. The server's persisted name always wins; the
 * derived one is a deterministic function of the persisted id, so it is the same
 * on every reload and on every device.
 */
export function villagerName(id: string, given?: string | null): string {
  const trimmed = given?.trim();
  if (trimmed) return trimmed;
  return VILLAGER_NAMES[hashId(id) % VILLAGER_NAMES.length];
}

type Facing = 'down' | 'side' | 'up';
type DirTex = Record<Facing, Texture[]>;
interface Anim { idle: DirTex; walk: DirTex; work: DirTex }

/** idle → travel (walking to a node) → work (swinging at it) → idle → … */
type State = 'idle' | 'walk' | 'work';
interface Ent {
  s: Sprite;
  anim: Anim;
  homeX: number; homeY: number;
  /** How far it strays from its anchor: tight when it has a job, loose when idle. */
  radius: number;
  state: State; wait: number;
  tgtX: number; tgtY: number;
  animT: number; frame: number; faceLeft: boolean; facing: Facing;
  /** Job kind when assigned (forester/miner/forager); null = idle villager. */
  job: string | null;
  /** The node being walked to / worked, so the swing faces it. */
  nodeX: number; nodeY: number;
  /** Guards against walking forever at something unreachable. */
  travelMs: number;
  /** A fixture, not a wanderer: the Forager Hut's keeper never leaves his counter. */
  pinned: boolean;
}

/**
 * Finds the nearest thing of `kinds` within `radiusTiles` of (wx, wy), in world
 * px. Supplied by the farm scene, which owns the decor index — this module has
 * no business knowing how the world is stored.
 */
export type FindNode = (kinds: string[], wx: number, wy: number, radiusTiles: number)
  => { x: number; y: number } | null;

export interface Villagers {
  update(dt: number, blocked: (wx: number, wy: number) => boolean): void;
  destroy(): void;
}

const rand = (a: number, b: number) => a + Math.random() * (b - a);

async function sliceStrip(url: string): Promise<Texture[]> {
  const base = (await Assets.load(url)) as Texture;
  const fw = base.width / FRAMES;
  const out: Texture[] = [];
  for (let i = 0; i < FRAMES; i++) {
    out.push(new Texture({ source: base.source, frame: new Rectangle(i * fw, 0, fw, base.height) }));
  }
  return out;
}

/**
 * `pinned` is how a shopkeeper is expressed without a second NPC renderer: same
 * strips, same idle loop, same y-sorting — it simply never picks a target, so it
 * stands at its tile breathing instead of strolling off mid-sale.
 */
export async function createVillagers(
  list: { kind: string; tx: number; ty: number; jobTx?: number | null; jobTy?: number | null; pinned?: boolean; facing?: Facing }[],
  host: Container,
  findNode?: FindNode,
): Promise<Villagers> {
  host.sortableChildren = true;
  const animCache = new Map<string, Anim>();
  const animFor = async (kind: string): Promise<Anim> => {
    const hit = animCache.get(kind);
    if (hit) return hit;
    const dir = async (anim: string): Promise<DirTex> => {
      const [down, side, up] = await Promise.all([
        sliceStrip(`/sprites/npc/${kind}_${anim}_down.png`),
        sliceStrip(`/sprites/npc/${kind}_${anim}_side.png`),
        sliceStrip(`/sprites/npc/${kind}_${anim}_up.png`),
      ]);
      return { down, side, up };
    };
    // A missing work strip must not sink the villager — fall back to idle so an
    // un-baked sheet degrades to standing still rather than an invisible NPC.
    const [idle, walk, work] = await Promise.all([dir('idle'), dir('walk'), dir('work').catch(() => null)]);
    const a: Anim = { idle, walk, work: work ?? idle };
    animCache.set(kind, a);
    return a;
  };

  const ents: Ent[] = [];
  for (const v of list) {
    const anim = await animFor(v.kind);
    const s = new Sprite(anim.idle.down[0]);
    // OWNER BUG (2026-08-14): "when i'm trying to click on the market, my hero is
    // moving, not taping on the market". The Forager Hut spawns its keeper one
    // tile SOUTH of the hut; his frame is 34x38 at scale 2.4, so his hit rect is
    // ~82x91 and covers the whole bottom half of the hut art, and his zIndex is a
    // full tile greater — so he is hit-tested first and swallowed every tap on the
    // shop. Villagers are display-only (nothing in this file binds a handler), so
    // they must never be hit-testable at all.
    s.eventMode = 'none';
    s.anchor.set(0.5, 1);
    s.scale.set(SCALE);
    // An assigned villager anchors to its WORKPLACE tile and barely strays from
    // it — you can see who is working what. An idle one mills about its home.
    const working = v.jobTx != null && v.jobTy != null;
    const ax = working ? v.jobTx! : v.tx, ay = working ? v.jobTy! : v.ty;
    const homeX = ax * TILE + TILE / 2, homeY = ay * TILE + TILE;
    s.x = homeX; s.y = homeY; s.zIndex = homeY;
    host.addChild(s);
    ents.push({
      s, anim, homeX, homeY,
      radius: v.pinned ? 0 : WANDER_RADIUS,
      state: 'idle', wait: rand(PAUSE.min, PAUSE.max),
      tgtX: homeX, tgtY: homeY,
      animT: 0, frame: 0, faceLeft: false, facing: v.facing ?? 'down',
      // Only an ASSIGNED villager works; an unemployed one has nothing to go and do.
      job: working && !v.pinned ? v.kind : null,
      nodeX: 0, nodeY: 0, travelMs: 0,
      pinned: !!v.pinned,
    });
  }

  /**
   * Decide what to do next. A worker looks for a node of its trade near its
   * workplace and heads for it; if it cannot find one (nothing left in range, or
   * no lookup wired) it falls through to the ordinary stroll, so a forester in a
   * clear-felled meadow still behaves like a person rather than freezing.
   */
  function pickTarget(e: Ent, blocked: (x: number, y: number) => boolean) {
    if (e.job && findNode) {
      const kinds = JOB_TARGETS[e.job];
      const node = kinds ? findNode(kinds, e.homeX, e.homeY, SEARCH_TILES) : null;
      if (node) {
        e.nodeX = node.x; e.nodeY = node.y;
        // Stand just short of the node: its own tile is solid, so walking AT it
        // would stall against the collision grid one step away and look stuck.
        const dx = node.x - e.s.x, dy = node.y - e.s.y;
        const d = Math.hypot(dx, dy) || 1;
        e.tgtX = node.x - (dx / d) * WORK_REACH;
        e.tgtY = node.y - (dy / d) * WORK_REACH;
        e.state = 'walk';
        e.travelMs = 0;
        return;
      }
    }
    for (let tries = 0; tries < 8; tries++) {
      const ang = Math.random() * Math.PI * 2;
      const r = rand(Math.min(1, e.radius * 0.5), e.radius) * TILE;
      const tx = e.homeX + Math.cos(ang) * r, ty = e.homeY + Math.sin(ang) * r;
      if (!blocked(tx, ty)) { e.tgtX = tx; e.tgtY = ty; e.state = 'walk'; return; }
    }
    e.wait = rand(PAUSE.min, PAUSE.max);   // boxed in — idle a bit longer, try again later
  }

  function update(dt: number, blocked: (wx: number, wy: number) => boolean) {
    for (const e of ents) {
      if (e.state === 'idle') {
        // A pinned keeper skips target picking entirely — no wander, no facing
        // change — but still falls through to the animation block below, so he
        // plays his idle loop rather than freezing on frame 0.
        if (!e.pinned) {
          e.wait -= dt * 1000;
          if (e.wait <= 0) pickTarget(e, blocked);
        }
      } else if (e.state === 'work') {
        // Swinging. Face the node rather than the last walk direction, so the
        // axe lands on the tree instead of off into the grass.
        e.wait -= dt * 1000;
        const ndx = e.nodeX - e.s.x, ndy = e.nodeY - e.s.y;
        if (Math.abs(ndx) > Math.abs(ndy)) { e.facing = 'side'; e.faceLeft = ndx < 0; }
        else e.facing = ndy < 0 ? 'up' : 'down';
        if (e.wait <= 0) { e.state = 'idle'; e.wait = rand(PAUSE.min, PAUSE.max); }
      } else {
        const dx = e.tgtX - e.s.x, dy = e.tgtY - e.s.y;
        const dist = Math.hypot(dx, dy);
        // Walking to a node can fail: it may sit across water, or behind a fence
        // the villager will not path around (they walk straight lines). Without a
        // timeout that villager grinds against the obstacle for the rest of the
        // session, which is exactly the "assigned but doing nothing" complaint.
        if (e.job) e.travelMs += dt * 1000;
        if (e.job && e.travelMs > TRAVEL_TIMEOUT_MS) {
          e.state = 'idle'; e.wait = rand(PAUSE.min, PAUSE.max); e.travelMs = 0;
        } else if (dist < 2) {
          // Arrived. A worker that came for a node starts swinging at it; anyone
          // else was just strolling and simply stops.
          const atNode = e.job && Math.hypot(e.nodeX - e.s.x, e.nodeY - e.s.y) <= WORK_REACH * 2;
          if (atNode) { e.state = 'work'; e.wait = rand(WORK_MS.min, WORK_MS.max); e.frame = 0; }
          else { e.state = 'idle'; e.wait = rand(PAUSE.min, PAUSE.max); }
        } else {
          const step = SPEED * TILE * dt;
          const nx = e.s.x + (dx / dist) * step, ny = e.s.y + (dy / dist) * step;
          if (blocked(nx, ny)) { e.state = 'idle'; e.wait = rand(400, 1200); }
          else {
            e.s.x = nx; e.s.y = ny;
            // Face where it walks (dominant axis): sideways → the side strip (flipped
            // for left), up/down → the back/front strip. This is what stops every
            // villager staring at the camera while walking away.
            if (Math.abs(dx) > Math.abs(dy)) { e.facing = 'side'; e.faceLeft = dx < 0; }
            else e.facing = dy < 0 ? 'up' : 'down';
          }
        }
      }
      // Animate + y-sort + face.
      const frames = (e.state === 'walk' ? e.anim.walk : e.state === 'work' ? e.anim.work : e.anim.idle)[e.facing];
      const fps = e.state === 'walk' ? WALK_FPS : e.state === 'work' ? WORK_FPS : IDLE_FPS;
      e.animT += dt;
      if (e.animT >= 1 / fps) { e.animT = 0; e.frame = (e.frame + 1) % frames.length; }
      e.s.texture = frames[e.frame % frames.length];
      // Only the side view mirrors; down/up face straight so never flip them.
      e.s.scale.x = (e.facing === 'side' && e.faceLeft) ? -SCALE : SCALE;
      e.s.zIndex = e.s.y;
    }
  }

  function destroy() {
    for (const e of ents) e.s.destroy();
    ents.length = 0;
  }

  return { update, destroy };
}
